import { useEffect, useMemo, useState } from 'react';
import { en } from '../../locales/en';
import { useGcsStore } from '../../store/useGcsStore';
import { useSelectedVehicleStore } from '../../store/useSelectedVehicleStore';
import { AsyncAction } from '../../types/general/asyncAction';
import { AppError, ErrorCode, ErrorDomain } from '../../types/errors/errors';
import type { VehicleId, VehicleModuleVersion } from '../../types/gcs/vehicle';
import { useLocalizedErrorMessage } from '../../hooks/useLocalizedErrorMessage';
import { getShortVehicleId } from '../../utils/formatters';
import Dialog from './Dialog';
import { Spinner } from '../general/Spinner';
import { Text } from '../general/Text';
import VehicleMissionLabel from '../general/VehicleMissionLabel';
import { VersionRow } from '../general/VersionRow';

interface VehicleModuleVersionsDialogProps {
  vehicleId: VehicleId;
  onClose: () => void;
}

const orderedAllowedModules = ['mc', 'fc', 'fts'] as const;

function formatValue(value?: string | null): string {
  return value ?? en.common.notAvailable;
}

function VehicleModuleVersionsDialog({ vehicleId, onClose }: VehicleModuleVersionsDialogProps) {
  const getLocalizedErrorMessage = useLocalizedErrorMessage();
  const [fetchAction, setFetchAction] = useState<AsyncAction>(AsyncAction.loading());
  const [moduleVersions, setModuleVersions] = useState<readonly VehicleModuleVersion[]>([]);

  const selectedVehicle = useSelectedVehicleStore((state) => state.selectedVehicle);
  const gcsIdByVehicleId = useGcsStore((state) => {
    for (const [gcsId, gcsData] of state.data.entries()) {
      if (gcsData.vehicles.data?.some((vehicle) => vehicle.id === vehicleId)) {
        return gcsId;
      }
    }

    return undefined;
  });
  const fetchVehicleAllModuleVersions = useGcsStore((state) => state.fetchVehicleAllModuleVersions);

  const gcsId = useMemo(() => {
    if (selectedVehicle?.vehicleId === vehicleId) {
      return selectedVehicle.gcsId;
    }

    return gcsIdByVehicleId;
  }, [selectedVehicle, vehicleId, gcsIdByVehicleId]);

  const title = `Vehicle ${getShortVehicleId(vehicleId)} Versions`;

  const displayedModuleVersions = useMemo(() => {
    const moduleVersionByModule = new Map(moduleVersions.map((moduleVersion) => [moduleVersion.module, moduleVersion]));

    return orderedAllowedModules
      .map((module) => moduleVersionByModule.get(module))
      .filter((moduleVersion): moduleVersion is VehicleModuleVersion => moduleVersion !== undefined);
  }, [moduleVersions]);

  useEffect(() => {
    const loadVehicleModuleVersions = async () => {
      if (!gcsId) {
        setFetchAction(
          AsyncAction.failure(
            new AppError({
              domain: ErrorDomain.BUSINESS,
              code: ErrorCode.GCS_NOT_FOUND,
              params: { vehicleId }
            })
          )
        );
        return;
      }

      try {
        setFetchAction(AsyncAction.loading());
        const versions = await fetchVehicleAllModuleVersions(gcsId, vehicleId);
        setModuleVersions(versions);
        setFetchAction(AsyncAction.success());
      } catch (error) {
        setFetchAction(AsyncAction.failure(error));
      }
    };

    loadVehicleModuleVersions();
  }, [fetchVehicleAllModuleVersions, gcsId, vehicleId]);

  return (
    <Dialog
      title={title}
      onClose={onClose}
      errorText={fetchAction.isFailure ? getLocalizedErrorMessage(fetchAction.error) : undefined}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          width: 'var(--dialog-common-width)',
          textAlign: 'left'
        }}
      >
        <VehicleMissionLabel vehicleId={vehicleId} />

        {fetchAction.isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '24px 0'
            }}
          >
            <Spinner />
          </div>
        )}

        {fetchAction.isSuccess && displayedModuleVersions.length === 0 && (
          <Text variant='body2'>{en.vehicleModuleVersionsDialog.emptyState}</Text>
        )}

        {fetchAction.isSuccess && displayedModuleVersions.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              overflowY: 'auto'
            }}
          >
            {displayedModuleVersions.map((moduleVersion) => (
              <div key={moduleVersion.module} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <VersionRow label={moduleVersion.displayName} version={formatValue(moduleVersion.version)} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default VehicleModuleVersionsDialog;
